/*
  Show one wallet button under Checkout instead of the whole stack.

  The layout is NOT in the page. <shopify-accelerated-checkout-cart> attaches
  an open shadow root once it hydrates, and lays the wallets out in there:

    #shadow-root
      div.wallet-cart-grid                  flex
        div.wallet-cart-button-container    <- the box that holds the space
          div.wallet-cart-button
            <slot name="button-ApplePay">   <- the light-DOM wallet goes here

  The wallet elements stay in the light DOM and are slotted into those
  containers. So hiding a wallet from page CSS empties its slot and leaves the
  container standing as a flex item -- which is the empty space that survived
  seven attempts. Page CSS cannot reach a shadow node, so no stylesheet in this
  theme could ever have fixed it.

  The root is open, so this reaches in and hides the CONTAINER instead. Same
  technique hide-amazon-branding.js already uses, minus the attachShadow patch:
  Shopify opens this one itself.

  Two invariants, both learned the hard way:

  1. Every pass restores what it hid before choosing. A hidden wallet can never
     gain height, so without this a wallet hidden once is invisible to the
     measurement below forever -- that is how Apple Pay vanished entirely.
  2. Nothing is hidden until a wallet has actually painted. Worst case is the
     full stack showing, never an empty space where a button should be.
*/
(function () {
  var PRIORITY = [
    'shopify-apple-pay-button',
    'shopify-shop-pay-button',
    'shopify-google-pay-button',
    'shopify-paypal-button',
    'shopify-amazon-pay-button',
    'shopify-buy-with-prime-button'
  ];

  function containers(root) {
    return root.querySelectorAll('.wallet-cart-button-container');
  }

  /* Which container is this wallet slotted into? Resolved through the slot's
     own assignment rather than the element's slot attribute, so a change in
     Shopify's naming cannot silently break the match. */
  function containerFor(root, wallet) {
    var boxes = containers(root);
    for (var i = 0; i < boxes.length; i++) {
      var slots = boxes[i].querySelectorAll('slot');
      for (var j = 0; j < slots.length; j++) {
        var assigned = slots[j].assignedElements ? slots[j].assignedElements() : [];
        for (var k = 0; k < assigned.length; k++) {
          if (assigned[k] === wallet) return boxes[i];
        }
      }
    }
    return null;
  }

  function apply(block) {
    var host = block.querySelector('shopify-accelerated-checkout-cart');
    var root = host && host.shadowRoot;
    /* No shadow root yet: still the pre-hydration skeleton. Reported as
       unresolved so run() keeps checking -- see the note there. */
    if (!root) return false;

    var boxes = containers(root);
    if (!boxes.length) return false;

    /* Restore first, always -- see invariant 1. */
    for (var i = 0; i < boxes.length; i++) {
      if (boxes[i].hasAttribute('data-pf-hidden')) {
        boxes[i].style.display = '';
        boxes[i].removeAttribute('data-pf-hidden');
      }
    }

    /* Presence of a tag says nothing about support: Shopify ships every wallet
       element and paints only the ones this device can use. Height is the only
       honest signal, and reading it here forces layout, so the restore above is
       already reflected. */
    var winner = null;
    for (var p = 0; p < PRIORITY.length && !winner; p++) {
      var found = host.querySelectorAll(PRIORITY[p]);
      for (var f = 0; f < found.length; f++) {
        if (found[f].getBoundingClientRect().height > 0) { winner = found[f]; break; }
      }
    }
    if (!winner) return false;               /* invariant 2 */

    var keep = containerFor(root, winner);
    if (!keep) return false;

    /* One per row, exactly as wide as the Checkout button above it.

       Shopify gutters the row by pulling the grid out with `margin: -5px` and
       pushing each container back in with `margin: 5px`. That cancels out for
       a row of buttons, but a `width: 100%` on the container resolved against
       the grid's 490px content box and overhung Checkout's right edge by 10px
       -- visible as two buttons that do not line up.

       Zeroing both margins and setting no width at all is what fixes it: a
       column flex container stretches its items across the cross axis by
       default, so the button lands on exactly the block's width. */
    var grid = root.querySelector('.wallet-cart-grid');
    if (grid) {
      grid.style.flexDirection = 'column';
      grid.style.margin = '0';
    }
    keep.style.margin = '0';
    keep.style.width = '';

    for (var b = 0; b < boxes.length; b++) {
      if (boxes[b] === keep) continue;
      boxes[b].setAttribute('data-pf-hidden', '');
      boxes[b].style.display = 'none';
    }

    /* Shopify re-renders the shadow content on cart updates and takes these
       changes with it. Page-level observers never see that -- shadow mutations
       do not bubble out -- so the root needs its own. */
    if (!root.__pfWatched) {
      root.__pfWatched = true;
      new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
    }

    return true;
  }

  /* setTimeout, not requestAnimationFrame.

     rAF does not fire in a background tab, and an earlier version set a
     `scheduled` flag before calling it -- so one trigger while throttled left
     the flag stuck true and every later trigger no-opped for the rest of the
     page's life. Reproduced: add from an upsell row, and the wallets stayed
     stacked with no error logged, while calling the resolver by hand fixed it
     immediately. A timer owes nothing to paint scheduling. */
  var scheduled = 0;
  function schedule() {
    if (scheduled) return;
    scheduled = setTimeout(function () { scheduled = 0; run(); }, 50);
  }

  function run() {
    var blocks = document.querySelectorAll('.pf-wallet-buttons');
    var pending = false;

    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      /* A wallet is inserted at zero height and only gains its box when the
         iframe inside it loads -- in that iframe's own document, firing no
         mutation we can see. Watching the block's size catches the moment a
         button actually paints, and covers the drawer opening later too. */
      if (!block.__pfWatched && window.ResizeObserver) {
        block.__pfWatched = true;
        new ResizeObserver(schedule).observe(block);
      }
      if (!apply(block)) pending = true;
    }

    /* Keep checking while a block is unresolved.

       Adding an upsell from the drawer re-renders the section, so the block is
       replaced and its widget starts again from the pre-hydration skeleton.
       Neither observer can see it finish: the shadow root is attached without a
       childList mutation, and the new block is 50px from the start -- Shopify
       reserves the height -- so the ResizeObserver never fires either. Without
       this the wallets came back stacked after any add-to-cart, once the
       initial poll had expired. */
    if (pending) startPolling();
    else stopPolling();
  }

  var poller = null;
  var polls = 0;

  function startPolling() {
    if (poller) return;
    polls = 0;
    poller = setInterval(function () {
      /* Bounded: a device where no wallet ever paints must not poll forever.
         A later re-render restarts it through run(). */
      if (++polls > 120) { stopPolling(); return; }
      run();
    }, 500);
  }

  function stopPolling() {
    if (!poller) return;
    clearInterval(poller);
    poller = null;
  }

  run();
  document.addEventListener('DOMContentLoaded', schedule);
  window.addEventListener('load', schedule);
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  window.pfWalletResolve = run;
})();
